import React, { useEffect, useState } from 'react';
import { CheckIcon, GripVerticalIcon, PlusIcon, RefreshCwIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { createStage, setInitialStage, updateOrganization, updateStage } from '../../services/organizationService';
import { SECTOR_OPTIONS } from '../../data/sectors';
import { FONT_OPTIONS } from '../../data/fonts';
import { Organization } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { AdminSection } from '../../components/settings/AdminSection';
import { BrandLogo } from '../../components/shared/BrandLogo';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { toast } from '../../components/ui/Toast';
import { ensureDefaultContent } from '../../services/repair';
import { refresh } from '../../services/store';
export function Settings() {
  const {
    organization,
    isSuperAdmin
  } = useAuth();
  const {
    stages
  } = useWorkspace();
  const [draft, setDraft] = useState<Organization | null>(organization);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newStage, setNewStage] = useState('');
  const [syncing, setSyncing] = useState(false);
  useEffect(() => {
    if (organization && (!draft || draft.id !== organization.id)) setDraft(organization);
  }, [organization, draft]);
  if (!draft) return null;
  function update(partial: Partial<Organization>) {
    setDraft(prev => prev ? {
      ...prev,
      ...partial
    } : prev);
    setSaved(false);
  }
  async function handleSave() {
    setSaving(true);
    await updateOrganization(draft!.id, draft!);
    setSaving(false);
    setSaved(true);
  }
  /**
   * Sincroniza el contenido de esta organización con las plantillas oficiales:
   * completa el formulario predeterminado y las landings con lo que falte, sin
   * sobrescribir nada de lo personalizado. Es idempotente.
   */
  async function handleSync() {
    setSyncing(true);
    try {
      const result = await ensureDefaultContent(draft!.id, draft!.name);
      await refresh();
      toast.success('Proyecto actualizado', {
        description: result.addedQuestions > 0 ? `Se han añadido ${result.addedQuestions} preguntas que faltaban.` : 'Tus formularios y landings ya estaban al día.'
      });
    } catch (err) {
      toast.error('No se ha podido actualizar el proyecto', {
        description: err instanceof Error ? err.message : 'Error desconocido.'
      });
    } finally {
      setSyncing(false);
    }
  }
  return <>
      <PageHeader title="Configuración" description="Datos, identidad visual y preferencias de tu empresa." actions={<>
            {saved && <span className="flex items-center gap-1.5 self-center text-sm text-green-600" data-id="element-1493" data-mp="3pbnhsoedq2zo">
                <CheckIcon className="h-4 w-4" data-id="element-1494" data-mp="3kd3sqf4hob33" /> Guardado
              </span>}
            <Button variant="secondary" leftIcon={<RefreshCwIcon className="h-4 w-4" data-id="element-1496" data-mp="3al7saxexk8vp" />} onClick={handleSync} loading={syncing} data-id="element-1495" data-mp="3fi6lndqrsbsu">
              Actualizar proyecto
            </Button>
            <Button onClick={handleSave} loading={saving} data-id="element-1497" data-mp="35mmqitk15h55">
              Guardar cambios
            </Button>
          </>} data-id="element-1492" data-mp="24q8jj3dvrwsj" />

      <Tabs defaultValue="empresa" data-id="element-1498" data-mp="31mcmhmfst0sx">
        <TabsList data-id="element-1499" data-mp="2wkx3s113f6dp">
          <TabsTrigger value="empresa" data-id="element-1500" data-mp="2wm83nprf3tzj">Empresa</TabsTrigger>
          <TabsTrigger value="identidad" data-id="element-1501" data-mp="2rm27pxe47lbn">Branding</TabsTrigger>
          <TabsTrigger value="pipeline" data-id="element-1502" data-mp="2rnn47sjzpec5">Embudo de ventas</TabsTrigger>
          <TabsTrigger value="notificaciones" data-id="element-1503" data-mp="uu22wus14h04">Notificaciones</TabsTrigger>
          <TabsTrigger value="privacidad" data-id="element-1504" data-mp="puyv52knzwog">Privacidad</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="administracion" data-id="element-1505" data-mp="psw90tmmunp0">Administración</TabsTrigger>}
        </TabsList>

        <TabsContent value="empresa" data-id="element-1506" data-mp="3jkuid7cc1m7v">
          <div className="grid gap-4 rounded-lg border border-border bg-background p-5 sm:grid-cols-2" data-id="element-1507" data-mp="3nok4lq5jbrpq">
            <Input id="o-name" label="Nombre de empresa o persona" value={draft.name} onChange={e => update({
            name: e.target.value
          })} data-id="element-1508" data-mp="39panmo9i8tkp" />
            <Select id="o-sector" label="Sector" options={SECTOR_OPTIONS} value={draft.sector} onChange={e => update({
            sector: e.target.value
          })} data-id="element-1509" data-mp="3epf7mo9mg6fo" />
            <Input id="o-email" label="Email de contacto" type="email" value={draft.email ?? ''} onChange={e => update({
            email: e.target.value
          })} data-id="element-1510" data-mp="34qzdecfash1y" />
            <Input id="o-phone" label="Teléfono" value={draft.phone ?? ''} onChange={e => update({
            phone: e.target.value
          })} data-id="element-1511" data-mp="2vwb7ck1wevc3" />
            <Input id="o-web" label="Web" value={draft.website ?? ''} onChange={e => update({
            website: e.target.value
          })} data-id="element-1512" data-mp="2h1wwx7wegu91" />
            <Input id="o-address" label="Dirección" value={draft.address ?? ''} onChange={e => update({
            address: e.target.value
          })} data-id="element-1513" data-mp="22cd7iz0d841u" />
            <Select id="o-tz" label="Zona horaria" options={[{
            value: 'Europe/Madrid',
            label: 'Europe/Madrid'
          }, {
            value: 'Atlantic/Canary',
            label: 'Atlantic/Canary'
          }, {
            value: 'America/Mexico_City',
            label: 'America/Mexico_City'
          }, {
            value: 'America/Bogota',
            label: 'America/Bogota'
          }, {
            value: 'America/Argentina/Buenos_Aires',
            label: 'Buenos Aires'
          }]} value={draft.timezone} onChange={e => update({
            timezone: e.target.value
          })} data-id="element-1514" data-mp="1ydw7t0j9xvug" />
            <Input id="o-logo" label="Logo (URL)" value={draft.logoUrl ?? ''} onChange={e => update({
            logoUrl: e.target.value
          })} data-id="element-1515" data-mp="34zgo79r6coup" />
          </div>
        </TabsContent>

        <TabsContent value="identidad" data-id="element-1516" data-mp="374da9kkvhyfv">
          <div className="space-y-6 rounded-2xl border border-border bg-background p-6 sm:p-8" data-id="element-1517" data-mp="375elbp1w2kxl">
            <p className="text-sm text-muted-foreground" data-id="element-1518" data-mp="3c3y9lw3yg8t4">
              Este logotipo y estos colores se aplican automáticamente en el
              dashboard, el menú lateral, tus landings, tus formularios, el pie de
              página, la página de gracias y los emails.
            </p>

            <LogoUploader organizationId={draft.id} kind="logo" value={draft.brand.logoUrl ?? draft.logoUrl} onChange={value => update({
            logoUrl: value,
            brand: {
              ...draft.brand,
              logoUrl: value
            }
          })} data-id="element-1519" data-mp="3qr1928bu8rj3" />

            <Input id="b-name" label="Nombre de la empresa" value={draft.name} onChange={e => update({
            name: e.target.value
          })} data-id="element-1520" data-mp="8mzn7fd1k8bh" />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1521" data-mp="25gkls5dmarjt">
              <Input id="b-primary" label="Color principal" type="color" value={draft.brand.primaryColor} onChange={e => update({
              brand: {
                ...draft.brand,
                primaryColor: e.target.value
              }
            })} data-id="element-1522" data-mp="2aevz4q34fk6s" />
              <Input id="b-secondary" label="Color secundario" type="color" value={draft.brand.secondaryColor} onChange={e => update({
              brand: {
                ...draft.brand,
                secondaryColor: e.target.value
              }
            })} data-id="element-1523" data-mp="1t48reseegrq6" />
              <Input id="b-button" label="Color de botones" type="color" value={draft.brand.buttonColor ?? draft.brand.primaryColor} onChange={e => update({
              brand: {
                ...draft.brand,
                buttonColor: e.target.value
              }
            })} data-id="element-1524" data-mp="2lr38rgfx7azf" />
              <Input id="b-text" label="Color de textos" type="color" value={draft.brand.textColor ?? '#0B0B0F'} onChange={e => update({
              brand: {
                ...draft.brand,
                textColor: e.target.value
              }
            })} data-id="element-1525" data-mp="26vnebabfhd2i" />
              <Input id="b-bg" label="Color de fondo" type="color" value={draft.brand.backgroundColor ?? '#FFFFFF'} onChange={e => update({
              brand: {
                ...draft.brand,
                backgroundColor: e.target.value
              }
            })} data-id="element-1526" data-mp="1l3uanb4ocr1c" />
              <Input id="b-favicon" label="Favicon (URL)" value={draft.brand.faviconUrl ?? ''} onChange={e => update({
              brand: {
                ...draft.brand,
                faviconUrl: e.target.value
              }
            })} data-id="element-1527" data-mp="1g59d5jgt4lgl" />
              <Select id="b-font" label="Tipografía principal" options={FONT_OPTIONS} value={draft.brand.fontFamily} onChange={e => update({
              brand: {
                ...draft.brand,
                fontFamily: e.target.value
              }
            })} data-id="element-1528" data-mp="28tombpd9c9k0" />
              <Select id="b-font2" label="Tipografía secundaria" options={FONT_OPTIONS} value={draft.brand.secondaryFontFamily ?? draft.brand.fontFamily} onChange={e => update({
              brand: {
                ...draft.brand,
                secondaryFontFamily: e.target.value
              }
            })} data-id="element-1529" data-mp="2mtfxlice3bub" />
              <Input id="b-domain" label="Dominio propio" placeholder="paginas.miempresa.com" value={draft.brand.domain ?? ''} onChange={e => update({
              brand: {
                ...draft.brand,
                domain: e.target.value
              }
            })} data-id="element-1530" data-mp="b6m6hykmewab" />
            </div>

            <div className="rounded-xl border border-border bg-surface p-5" data-id="element-1531" data-mp="3td0s8jp4saj9">
              <p className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground" data-id="element-1532" data-mp="3oeh7gmm9x4sh">
                Vista previa
              </p>
              <div className="flex flex-wrap items-center gap-4 rounded-lg px-5 py-4" style={{
              backgroundColor: draft.brand.backgroundColor ?? '#FFFFFF',
              fontFamily: `${draft.brand.fontFamily}, Inter, sans-serif`
            }} data-id="element-1533" data-mp="72y0vlfjuarp">
                <BrandLogo src={draft.brand.logoUrl ?? draft.logoUrl} size={24} data-id="element-1534" data-mp="1dkl3zpnjz6fz" />
                <span className="text-sm font-semibold" style={{
                color: draft.brand.secondaryColor
              }} data-id="element-1535" data-mp="jyhuolthx0sh">
                  {draft.name}
                </span>
                <span className="text-sm" style={{
                color: draft.brand.textColor ?? '#5F6673',
                fontFamily: `${draft.brand.secondaryFontFamily ?? draft.brand.fontFamily}, Inter, sans-serif`
              }} data-id="element-1536" data-mp="a7vf2rls4a9s">
                  Texto de ejemplo
                </span>
                <span className="ml-auto inline-flex h-9 items-center rounded-lg px-4 text-sm font-medium text-white" style={{
                backgroundColor: draft.brand.buttonColor ?? draft.brand.primaryColor
              }} data-id="element-1537" data-mp="3sdi8carzo6wo">
                  Botón principal
                </span>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pipeline" data-id="element-1538" data-mp="1llhfj8x39yam">
          <div className="rounded-lg border border-border bg-background p-5" data-id="element-1539" data-mp="1gnz85mypzr4q">
            <ul className="space-y-2" data-id="element-1540" data-mp="1o7zz3n3znn12">
              {stages.map(stage => <li key={stage.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3" data-id="element-1541" data-mp="1o7gq4vwbg8z6">
                  <GripVerticalIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-1542" data-mp="1t509ga7vfbxz" />
                  <Input id={`s-name-${stage.id}`} value={stage.name} onChange={e => updateStage(stage.id, {
                name: e.target.value
              })} data-id="element-1543" data-mp="1xzw83t7m4xk2" />
                  <input type="color" aria-label={`Color de ${stage.name}`} value={stage.color} onChange={e => updateStage(stage.id, {
                color: e.target.value
              })} className="h-9 w-12 cursor-pointer rounded-md border border-border bg-background" data-id="element-1544" data-mp="15afkd8uxu4u6" />
                  <Switch id={`s-active-${stage.id}`} label="Activo" checked={stage.active} onChange={checked => updateStage(stage.id, {
                active: checked
              })} data-id="element-1545" data-mp="1k6ojmaetwi6l" />
                  <Button size="sm" variant={stage.isInitial ? 'primary' : 'ghost'} onClick={() => setInitialStage(stage.organizationId, stage.id)} data-id="element-1546" data-mp="2vl22t1yn09ga">
                    {stage.isInitial ? 'Estado inicial' : 'Marcar como inicial'}
                  </Button>
                </li>)}
            </ul>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row" data-id="element-1547" data-mp="14jql4zcs2q03">
              <Input id="new-stage" placeholder="Nombre del nuevo estado" value={newStage} onChange={e => setNewStage(e.target.value)} data-id="element-1548" data-mp="14hwaqez4uga9" />
              <Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1550" data-mp="ctvxd74kj8dj" />} disabled={!newStage.trim()} onClick={async () => {
              await createStage(draft.id, newStage.trim(), '#3D8BFF');
              setNewStage('');
            }} data-id="element-1549" data-mp="csb1ne6ipje3">
                Añadir estado
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notificaciones" data-id="element-1551" data-mp="gsv0qnanix2v">
          <div className="space-y-4 rounded-lg border border-border bg-background p-5" data-id="element-1552" data-mp="3p9ziau71n27c">
            {([['newLead', 'Nuevo lead'], ['leadAssigned', 'Lead asignado'], ['statusChange', 'Cambio de estado'], ['newMeeting', 'Nueva reunión']] as const).map(([key, label]) => <Switch key={key} id={`n-${key}`} label={label} checked={draft.notifications[key]} onChange={checked => update({
            notifications: {
              ...draft.notifications,
              [key]: checked
            }
          })} data-id="element-1553" data-mp="3b3ed3ea5famp" />)}
          </div>
        </TabsContent>

        <TabsContent value="privacidad" data-id="element-1554" data-mp="3664ep94s7omq">
          <div className="space-y-4 rounded-lg border border-border bg-background p-5" data-id="element-1555" data-mp="3tv74x5dhepsu">
            <Input id="p-privacy" label="URL de la política de privacidad" value={draft.privacy.privacyPolicyUrl} onChange={e => update({
            privacy: {
              ...draft.privacy,
              privacyPolicyUrl: e.target.value
            }
          })} data-id="element-1556" data-mp="3ttcljvhuz5qv" />
            <Input id="p-legal" label="URL del aviso legal" value={draft.privacy.legalNoticeUrl} onChange={e => update({
            privacy: {
              ...draft.privacy,
              legalNoticeUrl: e.target.value
            }
          })} data-id="element-1557" data-mp="3ouj63xj0h4o2" />
            <Textarea id="p-consent" label="Texto de consentimiento" rows={3} value={draft.privacy.consentText} onChange={e => update({
            privacy: {
              ...draft.privacy,
              consentText: e.target.value
            }
          })} data-id="element-1558" data-mp="2tntozwbanq6d" />
            <Textarea id="p-cookies" label="Texto de cookies" rows={3} value={draft.privacy.cookiesText} onChange={e => update({
            privacy: {
              ...draft.privacy,
              cookiesText: e.target.value
            }
          })} data-id="element-1559" data-mp="2jw60o4a1ap94" />
          </div>
        </TabsContent>

        {/* Solo para el super administrador de la plataforma. */}
        {isSuperAdmin && <TabsContent value="administracion" data-id="element-1560" data-mp="32ngsqorhx82l">
            <AdminSection data-id="element-1561" data-mp="2xpovdf830tr0" />
          </TabsContent>}
      </Tabs>
    </>;
}